import ReactInputMask from 'react-input-mask'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { Controller, type UseFormReturn } from 'react-hook-form'

import { formatDocument } from '../../utils/document'
import { TOAST_ERROR_DEFAULT_OPTIONS } from '../../constants/toast'
import { api } from '../../services/api'
import { getErrorMessage } from '../../utils/getErrorMessage'
import { Input } from '../FormElements/Input'
import { Button } from '../Button'
import { isAxiosError } from 'axios'
import { useAuth } from '../../contexts/AuthContext'
import { isEmailAvailable } from '../../utils/isEmailAvailable'
import { isCpfAvailable } from '../../utils/isCpfAlreadyInUse'

type NaturalFormPresentationalProps = UseFormReturn<any> & {
  isSubmitting: boolean
  submitButtonText: string
  onCancel: () => void
  onSubmit: (data: NaturalFormData) => void
}

export type NaturalFormData = {
  cpf: string
  name: string
  birthdate: string
  rg: string
  email: string
  phone: string
  linkedin?: string
  cep: string
  street: string
  addressNumber: string
  complement: string
  neighborhood: string
  city: string
  state: string
}

export const NaturalFormPresentational = ({
  isSubmitting,
  submitButtonText,
  onCancel,
  onSubmit,
  ...props
}: NaturalFormPresentationalProps) => {
  const { formatMessage } = useIntl()
  const { user } = useAuth()

  const validateCpf = async (cpf: string) => {
    try {
      const formattedCpfFieldValue = formatDocument(cpf)

      if (formattedCpfFieldValue) {
        const { data } = await api.get(
          `/experts/validate-cpf/${formattedCpfFieldValue}`
        )

        props.setValue('name', data.name)
        props.setValue('birthdate', data.birthdate)
        props.clearErrors('cpf')
      }
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.data.message === 'CPF not found') {
          props.setError('cpf', { message: 'INVALID_CPF' })
          return
        }
      }

      toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
    }
  }

  const validateCep = async (cep: string) => {
    try {
      const formattedCepFieldValue = formatDocument(cep)

      if (formattedCepFieldValue) {
        const { data } = await api.get(
          `https://viacep.com.br/ws/${formattedCepFieldValue}/json/`
        )

        if (!data.erro) {
          props.setValue('street', data.logradouro)
          props.setValue('city', data.localidade)
          props.setValue('state', data.uf)
          props.setValue('neighborhood', data.bairro)
          props.clearErrors('cep')
          return
        }

        props.setValue('city', '')
        props.setValue('state', '')
        props.setValue('neighborhood', '')
        props.setValue('street', '')
        props.setError('cep', { message: 'INVALID_CEP' })
      }
    } catch (error) {
      toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
    }
  }

  return (
    <form onSubmit={props.handleSubmit(onSubmit)}>
      <div className="w-full flex flex-col gap-[20px]">
        <div className="w-36 md:w-44">
          <Controller
            name="cpf"
            control={props.control}
            render={({
              field: { value = '', onChange },
              fieldState: { error },
            }) => (
              <ReactInputMask
                mask="999.999.999-99"
                maskPlaceholder={null}
                value={value}
                onChange={async (event) => {
                  onChange(event)

                  const currentValue = event.target.value
                  const cpfPattern = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/

                  if (cpfPattern.test(currentValue)) {
                    if (await isCpfAvailable(currentValue, user?.cpf)) {
                      props.clearErrors('cpf')
                      validateCpf(currentValue)
                      return
                    }

                    props.setError('cpf', { message: 'CPF_EXISTS' })
                  }
                }}
              >
                <Input
                  label="CPF"
                  placeholder="000.000.000-00"
                  isValid={false}
                  errorMessage={
                    error?.message && formatMessage({ id: error.message })
                  }
                />
              </ReactInputMask>
            )}
          />
        </div>

        <Controller
          name="name"
          control={props.control}
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => (
            <Input
              label={formatMessage({ id: 'FULL_NAME' })}
              placeholder={formatMessage({ id: 'FULL_NAME' })}
              isValid={false}
              value={value}
              onChange={onChange}
              errorMessage={
                error?.message && formatMessage({ id: `${error?.message}` })
              }
            />
          )}
        />

        <div className="w-36 md:w-44">
          <Controller
            name="birthdate"
            control={props.control}
            render={({
              field: { value = '', onChange },
              fieldState: { error },
            }) => (
              <ReactInputMask
                mask="99/99/9999"
                maskPlaceholder={null}
                value={value}
                onChange={onChange}
              >
                <Input
                  label={formatMessage({ id: 'DATE_OF_BIRTH' })}
                  placeholder="00/00/0000"
                  isValid={false}
                  errorMessage={
                    error?.message && formatMessage({ id: `${error?.message}` })
                  }
                />
              </ReactInputMask>
            )}
          />
        </div>

        <div className="w-36 md:w-44">
          <Controller
            name="rg"
            control={props.control}
            render={({
              field: { value = '', onChange },
              fieldState: { error },
            }) => (
              <Input
                value={value}
                onChange={onChange}
                label="RG"
                placeholder="00.000.000-0"
                isValid={false}
                errorMessage={
                  error?.message && formatMessage({ id: `${error?.message}` })
                }
              />
            )}
          />
        </div>

        <div className="flex flex-col gap-5 md:flex-row md:gap-2">
          <div className="md:grow">
            <Controller
              name="email"
              control={props.control}
              render={({
                field: { value = '', onChange },
                fieldState: { error },
              }) => (
                <Input
                  label="E-mail"
                  placeholder="exemple@email.com"
                  isValid={false}
                  value={value}
                  onChange={async (event) => {
                    onChange(event)

                    const emailFieldValue = event.target.value
                    const currentUserEmail = user?.email
                    const isNotEmailAvailable = !(await isEmailAvailable(
                      emailFieldValue,
                      currentUserEmail
                    ))

                    if (isNotEmailAvailable) {
                      props.setError('email', {
                        message: 'EMAIL_ALREADY_IN_USE',
                      })
                      return
                    }

                    props.clearErrors('email')
                  }}
                  errorMessage={
                    error?.message && formatMessage({ id: `${error?.message}` })
                  }
                />
              )}
            />
          </div>
          <div className="w-36 md:w-44">
            <Controller
              name="phone"
              control={props.control}
              render={({
                field: { value = '', onChange },
                fieldState: { error },
              }) => (
                <ReactInputMask
                  mask="(99) 99999-9999"
                  maskPlaceholder={null}
                  value={value}
                  onChange={onChange}
                >
                  <Input
                    label={formatMessage({ id: 'TELEPHONE' })}
                    placeholder="(00) 00000-0000"
                    isValid={false}
                    errorMessage={
                      error?.message &&
                      formatMessage({ id: `${error?.message}` })
                    }
                  />
                </ReactInputMask>
              )}
            />
          </div>
        </div>

        <Controller
          name="linkedin"
          control={props.control}
          render={({
            field: { value = '', onChange },
            fieldState: { error },
          }) => (
            <Input
              label="LinkedIn"
              placeholder="https://www.linkedin.com/in/example-user-name"
              isValid={false}
              value={value}
              onChange={onChange}
              errorMessage={
                error?.message && formatMessage({ id: `${error?.message}` })
              }
            />
          )}
        />

        <div className="w-36 md:w-44">
          <Controller
            name="cep"
            control={props.control}
            render={({
              field: { value = '', onChange },
              fieldState: { error },
            }) => (
              <ReactInputMask
                mask="99999-999"
                maskPlaceholder={null}
                value={value}
                onChange={(event) => {
                  onChange(event)

                  const currentValue = event.target.value
                  const cepPattern = /^\d{5}-\d{3}$/

                  if (cepPattern.test(currentValue)) {
                    validateCep(currentValue)
                  }
                }}
              >
                <Input
                  label="CEP"
                  placeholder="00000-000"
                  isValid={false}
                  errorMessage={
                    error?.message && formatMessage({ id: error.message })
                  }
                />
              </ReactInputMask>
            )}
          />
        </div>
        <div>
          <Controller
            name="street"
            control={props.control}
            render={({
              field: { value = '', onChange },
              fieldState: { error },
            }) => (
              <Input
                label={formatMessage({ id: 'STREET' })}
                placeholder={formatMessage({ id: 'STREET' })}
                isValid={false}
                value={value}
                onChange={onChange}
                errorMessage={
                  error?.message && formatMessage({ id: `${error?.message}` })
                }
              />
            )}
          />
        </div>

        <div className="flex gap-2">
          <div className="w-20 md:w-28">
            <Controller
              name="addressNumber"
              control={props.control}
              render={({
                field: { value = '', onChange },
                fieldState: { error },
              }) => (
                <Input
                  label={formatMessage({ id: 'NUMBER' })}
                  placeholder="000"
                  isValid={false}
                  value={value}
                  onChange={onChange}
                  errorMessage={
                    error?.message && formatMessage({ id: `${error?.message}` })
                  }
                />
              )}
            />
          </div>
          <div className="grow">
            <Controller
              name="complement"
              control={props.control}
              render={({
                field: { value = '', onChange },
                fieldState: { error },
              }) => (
                <Input
                  label={formatMessage({ id: 'COMPLEMENT' })}
                  placeholder={formatMessage({ id: 'COMPLEMENT' })}
                  isValid={false}
                  value={value}
                  onChange={onChange}
                  errorMessage={
                    error?.message && formatMessage({ id: `${error?.message}` })
                  }
                />
              )}
            />
          </div>
        </div>

        <div className="flex flex-col gap-5 md:flex-row md:gap-2">
          <div className="md:grow">
            <Controller
              name="neighborhood"
              control={props.control}
              render={({
                field: { value = '', onChange },
                fieldState: { error },
              }) => (
                <Input
                  label={formatMessage({ id: 'NEIGHBORHOOD' })}
                  placeholder={formatMessage({ id: 'NEIGHBORHOOD' })}
                  isValid={false}
                  value={value}
                  onChange={onChange}
                  errorMessage={
                    error?.message && formatMessage({ id: `${error?.message}` })
                  }
                />
              )}
            />
          </div>
          <div className="md:grow">
            <Controller
              name="city"
              control={props.control}
              render={({
                field: { value = '', onChange },
                fieldState: { error },
              }) => (
                <Input
                  label={formatMessage({ id: 'CITY' })}
                  placeholder={formatMessage({ id: 'CITY' })}
                  isValid={false}
                  value={value}
                  onChange={onChange}
                  errorMessage={
                    error?.message && formatMessage({ id: `${error?.message}` })
                  }
                />
              )}
            />
          </div>
          <div className="md:grow">
            <Controller
              name="state"
              control={props.control}
              render={({
                field: { value = '', onChange },
                fieldState: { error },
              }) => (
                <Input
                  label={formatMessage({ id: 'STATE' })}
                  placeholder={formatMessage({ id: 'STATE' })}
                  isValid={false}
                  value={value}
                  onChange={onChange}
                  errorMessage={
                    error?.message && formatMessage({ id: `${error?.message}` })
                  }
                />
              )}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-row gap-2 mt-14">
        <Button type="button" variant="outline" onClick={onCancel}>
          {formatMessage({ id: 'CANCEL' })}
        </Button>

        <Button
          type="submit"
          isLoading={isSubmitting}
          disabled={
            !props.formState.isValid ||
            Object.entries(props.formState.errors).length > 0
          }
        >
          {submitButtonText}
        </Button>
      </div>
    </form>
  )
}
